package com.steamstreet.vegasful.browser.account.intelligence

import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlin.js.Date
import kotlin.js.json

/**
 * Carries a question asked somewhere else on the site to the concierge page.
 *
 * The question travels in local storage rather than in the URL. A URL is reported to analytics as
 * the page location, and `docs/analytics-plan.md` keeps the text of a question out of that stream;
 * it would also end up in server logs and in the browser's history.
 *
 * Local storage rather than session storage, because the question has to survive signing in. The
 * social providers redirect back in the same tab, which session storage would survive, but an
 * emailed sign-in link usually opens in a new tab, where it would not. The hold expires after
 * [HOLD_MS] so a question nobody finished asking does not ambush a later visit, and it is removed
 * as soon as the concierge page takes it.
 */
object ConciergeHandoff {
    private const val KEY = "vegasful.concierge.held"
    private const val HOLD_MS = 30 * 60 * 1000.0

    /** The page that answers held questions. */
    const val CONCIERGE_PATH = "/intelligence"

    /**
     * The concierge's example questions. The home hero types them into its box in concierge mode,
     * and the concierge page types them into its own.
     */
    val EXAMPLE_QUESTIONS = listOf(
        "What’s happening tonight?",
        "Any rooftop shows this weekend?",
        "Find me something low-key and fun.",
        "Where’s a good comedy show?",
        "Show me what’s electric in Vegas tonight."
    )

    /**
     * Hold [question] and open the concierge page, which asks it. A visitor who is not signed in
     * sees the page's sign-in panel first; the question waits for them.
     */
    fun ask(question: String) {
        if (question.isBlank()) return
        hold(question)
        window.location.href = CONCIERGE_PATH
    }

    /**
     * Hold [question] without going anywhere. The concierge page does this for a visitor who asks
     * before signing in, so the question is waiting for them once they have.
     */
    fun hold(question: String) {
        val text = question.trim()
        if (text.isEmpty()) return
        try {
            localStorage.setItem(KEY, JSON.stringify(json("q" to text, "t" to Date.now())))
        } catch (e: Throwable) {
            // Storage can be unavailable (a private window, blocked site data). The visitor still
            // reaches the concierge; they just type the question again.
            console.log("Unable to hold the concierge question")
        }
    }

    /** Whether a question is waiting to be asked. */
    fun isHeld(): Boolean = read() != null

    /** The held question, if there is one and it has not expired, left in place. */
    fun peek(): String? = read()

    /** Remove and return the held question, if there is one and it has not expired. */
    fun take(): String? {
        val question = read()
        clear()
        return question
    }

    private fun read(): String? {
        return try {
            val raw = localStorage.getItem(KEY) ?: return null
            val held = JSON.parse<dynamic>(raw)
            val question = held.q as? String
            val at = held.t as? Double
            if (question.isNullOrBlank() || at == null || Date.now() - at > HOLD_MS) {
                clear()
                null
            } else {
                question
            }
        } catch (e: Throwable) {
            null
        }
    }

    private fun clear() {
        try {
            localStorage.removeItem(KEY)
        } catch (_: Throwable) {
        }
    }
}
